// @flow
/* global grecaptcha */

import React, { type Node, useRef, useEffect, useReducer, useCallback } from 'react';
import { loadGrecaptchaFromScriptWithMaxTries } from '../grecaptcha';

type Props = {
  siteKey?: ?string,
  shouldResetCaptcha: boolean,
  setCaptchaToken: (string) => void,
};

export function Captcha({ siteKey, shouldResetCaptcha, setCaptchaToken, ...rest }: Props): Node {
  const captchaPlaceholderElement = useRef();

  const [loadingCaptchaState, dispatchChangeLoadingState] = useReducer(captchaLoadingStateReducer, {
    isLoading: true,
    loadingError: null,
    tries: 0,
  });

  useEffect(() => {
    (async () => {
      dispatchChangeLoadingState({ type: 'start' });
      const load = loadGrecaptchaFromScriptWithMaxTries(3);
      await load.execute();

      dispatchChangeLoadingState({
        type: 'finish',
        error: load.getLastError(),
        tries: load.triesCount(),
      });
    })();
  }, [dispatchChangeLoadingState]);

  const onCaptchaSolved = useCallback(token => {
    setCaptchaToken(token);
  }, [setCaptchaToken]);

  const onCaptchaReset = useCallback(() => {
    setCaptchaToken('');
  }, [setCaptchaToken]);

  useEffect(() => {
    if (!loadingCaptchaState.isLoading) {
      if (loadingCaptchaState.loadingError) {
        throw new Error(`Captcha - tried to load Grecaptcha ${loadingCaptchaState.tries} times - network problem.`);
      }

      grecaptcha.ready(() => {
        grecaptcha.render(
          captchaPlaceholderElement.current, {
            'sitekey': siteKey || ENV_CONSTANTS.RECAPTCHA_SITE_KEY,
            'callback': onCaptchaSolved,
            'expired-callback': onCaptchaReset,
            'error-callback': onCaptchaReset,
          }
        );
      });
    }
  }, [loadingCaptchaState, siteKey, onCaptchaSolved, onCaptchaReset]);

  useEffect(() => {
    if (shouldResetCaptcha) {
      try {
        if (typeof grecaptcha === 'object' && typeof grecaptcha.reset === 'function') {
          grecaptcha.reset();
        }
      } catch (e) {
        if (!e.message.startsWith('No reCAPTCHA clients exist')) {
          throw e;
        }
      }
    }
  }, [shouldResetCaptcha]);

  return (
    <div {...rest} ref={captchaPlaceholderElement} />
  );
}

type CaptchaLoadingState = {|
  isLoading: bool,
  loadingError: ?Error,
  tries: number,
|};

type ChangeCaptchaLoadingStateAction =
  | ChangeCaptchaLoadingStateStartAction
  | ChangeCaptchaLoadingStateFinishAction
;

type ChangeCaptchaLoadingStateStartAction = {|
  type: 'start',
|};

type ChangeCaptchaLoadingStateFinishAction = {|
  type: 'finish',
  error: ?Error,
  tries: number,
|};

function captchaLoadingStateReducer(state: CaptchaLoadingState, action: ChangeCaptchaLoadingStateAction): CaptchaLoadingState {
  switch (action.type) {
    case 'start':
      return { isLoading: true, loadingError: null, tries: 0 };
    case 'finish':
      return { isLoading: false, loadingError: action.error, tries: action.tries };
    default:
      return state;
  }
}
